import React, { useMemo, useCallback } from "react";
import { Inbox, Filter, CalendarDays, CalendarRange, Layers } from "lucide-react";
import OvertimeRequestCard from "./OvertimeRequestCard";

const OvertimeRequestsList = ({
  requests, theme, isRTL, t, language, onSelectRequest,
  dateRange, onDateRangeChange, overtimeLoading,
  // Persistent filter state from OvertimeDrawer
  filterMode, setFilterMode,
  selectedMonth, setSelectedMonth,
  selectedYear, setSelectedYear,
  selectedDay, setSelectedDay,
  activeStatusTab, setActiveStatusTab,
  employeeProfilePics,
}) => {
  const isDark = theme === "dark";
  const now = new Date();

  // ── Apply filters to API ──────────────────────────────────────────────
  const applyMonthFilter = useCallback((month, year) => {
    if (!onDateRangeChange) return;
    const start = new Date(year, month, 1).toISOString().split("T")[0];
    const endDate = new Date(year, month + 1, 0);
    const today = new Date();
    const end = (endDate > today ? today : endDate).toISOString().split("T")[0];
    onDateRangeChange({ start, end });
  }, [onDateRangeChange]);

  const applyDayFilter = useCallback((dayStr) => {
    if (!onDateRangeChange) return;
    onDateRangeChange({ start: dayStr, end: dayStr });
  }, [onDateRangeChange]);

  const applyAllFilter = useCallback(() => {
    if (!onDateRangeChange) return;
    const today = new Date();
    const start = new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0];
    const end = today.toISOString().split("T")[0];
    onDateRangeChange({ start, end });
  }, [onDateRangeChange]);

  const handleModeChange = useCallback((mode) => {
    setFilterMode(mode);
    if (mode === "month") applyMonthFilter(selectedMonth, selectedYear);
    else if (mode === "day") applyDayFilter(selectedDay);
    else applyAllFilter();
  }, [selectedMonth, selectedYear, selectedDay, applyMonthFilter, applyDayFilter, applyAllFilter, setFilterMode]);

  const handleMonthSelect = useCallback((e) => {
    const [y, m] = e.target.value.split("-").map(Number);
    setSelectedYear(y);
    setSelectedMonth(m - 1);
    applyMonthFilter(m - 1, y);
  }, [applyMonthFilter, setSelectedYear, setSelectedMonth]);

  const handleDaySelect = useCallback((e) => {
    setSelectedDay(e.target.value);
    applyDayFilter(e.target.value);
  }, [applyDayFilter, setSelectedDay]);

  // ── Status helpers ────────────────────────────────────────────────────
  const getStatus = (r) => {
    const overallStatus = r.overallStatus || "pending";
    return overallStatus;
  };

  const counts = useMemo(() => {
    let pending = 0, approved = 0, rejected = 0;
    requests.forEach((r) => {
      const s = getStatus(r);
      if (s === "pending") pending++; else if (s === "approved") approved++; else rejected++;
    });
    return { all: requests.length, pending, approved, rejected };
  }, [requests]);

  const filtered = useMemo(() => {
    if (activeStatusTab === "all") return requests;
    return requests.filter((r) => getStatus(r) === activeStatusTab);
  }, [requests, activeStatusTab]);

  // ── Computed values ───────────────────────────────────────────────────
  const monthInputValue = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
  const maxMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const maxDay = now.toISOString().split("T")[0];

  const filterModes = [
    { key: "month", label: language === "ar" ? "شهر" : "Month", icon: CalendarDays },
    { key: "day", label: language === "ar" ? "يوم" : "Day", icon: CalendarRange },
    { key: "all", label: language === "ar" ? "الجميع" : "All", icon: Layers },
  ];

  const tabs = [
    { key: "all", label: t.allRequests, count: counts.all, color: isDark ? "#94a3b8" : "#64748b" },
    { key: "pending", label: t.pendingApproval, count: counts.pending, color: "#f59e0b" },
    { key: "approved", label: t.approvedStatus, count: counts.approved, color: "#10b981" },
    { key: "rejected", label: t.rejectedStatus, count: counts.rejected, color: "#ef4444" },
  ];

  const filterDescription = useMemo(() => {
    if (filterMode === "month") {
      const d = new Date(selectedYear, selectedMonth, 1);
      const lbl = d.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { month: "long", year: "numeric" });
      return language === "ar" ? `عرض طلبات شهر ${lbl}` : `Showing requests for ${lbl}`;
    }
    if (filterMode === "day") {
      const d = new Date(selectedDay + "T00:00:00");
      const lbl = d.toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { weekday: "short", month: "long", day: "numeric" });
      return language === "ar" ? `عرض طلبات يوم ${lbl}` : `Requests for ${lbl}`;
    }
    return language === "ar" ? "عرض جميع الطلبات من بداية السنة حتى اليوم" : "All requests from year start to today";
  }, [filterMode, selectedYear, selectedMonth, selectedDay, language]);

  const emptyMessage = useMemo(() => {
    if (filterMode === "month") return language === "ar" ? "لا توجد طلبات وقت إضافي لهذا الشهر." : "No overtime requests found for this month.";
    if (filterMode === "day") return language === "ar" ? "لا توجد طلبات وقت إضافي لهذا اليوم." : "No overtime requests found for this day.";
    return language === "ar" ? "لا توجد طلبات وقت إضافي متاحة." : "No overtime requests available.";
  }, [filterMode, language]);

  const inputStyle = {
    width: "100%", padding: "10px 14px", borderRadius: "10px",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
    color: isDark ? "#e2e8f0" : "#1e293b", fontSize: "14px", fontWeight: 500,
    fontFamily: "'Cairo', sans-serif", outline: "none", cursor: "pointer",
    transition: "all 0.25s ease", direction: isRTL ? "rtl" : "ltr",
    colorScheme: isDark ? "dark" : "light", boxSizing: "border-box",
  };

  // ── Skeleton loader ───────────────────────────────────────────────────
  const SkeletonCard = () => (
    <div style={{
      padding: "16px", borderRadius: "14px",
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}`,
      display: "flex", alignItems: "center", gap: "14px",
      animation: "otPulse 1.5s ease-in-out infinite",
    }}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ width: "60%", height: 14, borderRadius: 6, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)", marginBottom: 8 }} />
        <div style={{ width: "40%", height: 10, borderRadius: 4, background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)" }} />
      </div>
      <div style={{ width: 50, height: 24, borderRadius: 8, background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", flexShrink: 0 }} />
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* ── Sticky Filter Card ──────────────────────────────────────── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10, padding: "20px 24px 0",
        background: isDark ? "linear-gradient(180deg, #0f172a 85%, transparent)" : "linear-gradient(180deg, #ffffff 85%, transparent)",
        paddingBottom: "16px",
      }}>
        <div style={{
          padding: "16px", borderRadius: "14px",
          background: isDark
            ? "rgba(255,255,255,0.04)"
            : "rgba(255,255,255,0.8)",
          backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
          boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.3)" : "0 4px 24px rgba(0,0,0,0.04)",
        }}>
          {/* Filter Header with count */}
          <div style={{
            display: "flex", alignItems: "center", gap: "8px",
            marginBottom: "12px", flexDirection: isRTL ? "row-reverse" : "row",
          }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: isDark ? "rgba(46,124,186,0.2)" : "rgba(46,124,186,0.1)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Filter size={14} color="#2e7cba" strokeWidth={2.5} />
            </div>
            <span style={{
              fontSize: "13px", fontWeight: 700,
              color: isDark ? "#e2e8f0" : "#1e293b",
              fontFamily: "'Cairo', sans-serif", flex: 1,
              textAlign: isRTL ? "right" : "left",
            }}>
              {language === "ar" ? "فلتر طلبات الوقت الإضافي" : "Overtime Request Filter"}
            </span>
            <span style={{
              padding: "2px 10px", borderRadius: "20px", fontSize: "12px", fontWeight: 700,
              background: isDark ? "rgba(46,124,186,0.15)" : "rgba(46,124,186,0.1)",
              color: "#2e7cba", fontFamily: "'Cairo', sans-serif",
            }}>
              {requests.length}
            </span>
          </div>

          {/* Mode Selector */}
          <div style={{
            display: "flex", gap: "3px", padding: "3px", borderRadius: "10px",
            background: isDark ? "rgba(0,0,0,0.35)" : "rgba(0,0,0,0.05)",
            marginBottom: filterMode !== "all" ? "12px" : "8px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}>
            {filterModes.map((mode) => {
              const isActive = filterMode === mode.key;
              const Icon = mode.icon;
              return (
                <button key={mode.key} onClick={() => handleModeChange(mode.key)} style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                  gap: "6px", padding: "8px 10px", borderRadius: "8px", border: "none",
                  background: isActive ? "linear-gradient(135deg, #2e7cba, #235c8c)" : "transparent",
                  color: isActive ? "#fff" : isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)",
                  fontSize: "12px", fontWeight: isActive ? 700 : 500, cursor: "pointer",
                  fontFamily: "'Cairo', sans-serif",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: isActive ? "0 2px 10px rgba(46,124,186,0.4)" : "none",
                  whiteSpace: "nowrap",
                }}>
                  <Icon size={14} />
                  {mode.label}
                </button>
              );
            })}
          </div>

          {/* Date Input with transition wrapper */}
          <div style={{
            overflow: "hidden",
            maxHeight: filterMode !== "all" ? "50px" : "0px",
            opacity: filterMode !== "all" ? 1 : 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            marginBottom: filterMode !== "all" ? "8px" : "0px",
          }}>
            {filterMode === "month" && (
              <input type="month" value={monthInputValue} max={maxMonth} onChange={handleMonthSelect} style={inputStyle} />
            )}
            {filterMode === "day" && (
              <input type="date" value={selectedDay} max={maxDay} onChange={handleDaySelect} style={inputStyle} />
            )}
          </div>

          {/* Filter Description */}
          <div style={{
            display: "flex", alignItems: "center", gap: "6px",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}>
            <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#2e7cba", flexShrink: 0 }} />
            <span style={{
              fontSize: "11px", fontWeight: 500, fontFamily: "'Cairo', sans-serif",
              color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
              textAlign: isRTL ? "right" : "left",
            }}>
              {filterDescription}
            </span>
            {overtimeLoading && (
              <span style={{
                fontSize: "11px", color: "#f59e0b", fontFamily: "'Cairo', sans-serif",
                fontWeight: 600, marginInlineStart: "auto",
              }}>
                {language === "ar" ? "جارٍ التحميل..." : "Loading..."}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Content Area (scrolls under sticky filter) ─────────────── */}
      <div style={{ flex: 1, padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: "12px" }}>

        {/* Summary Stats */}
        <div style={{ display: "flex", gap: "10px", flexDirection: isRTL ? "row-reverse" : "row" }}>
          {[
            { label: t.pendingApproval, count: counts.pending, color: "#f59e0b" },
            { label: t.approvedStatus, count: counts.approved, color: "#10b981" },
            { label: t.rejectedStatus, count: counts.rejected, color: "#ef4444" },
          ].map((stat) => (
            <div key={stat.label} style={{
              flex: 1, padding: "12px", borderRadius: "12px", textAlign: "center",
              background: isDark ? `${stat.color}10` : `${stat.color}08`,
              border: `1px solid ${isDark ? `${stat.color}20` : `${stat.color}15`}`,
            }}>
              <div style={{ fontSize: "22px", fontWeight: 800, color: stat.color, fontFamily: "'Cairo', sans-serif", lineHeight: 1 }}>
                {stat.count}
              </div>
              <div style={{ fontSize: "11px", fontWeight: 500, color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)", fontFamily: "'Cairo', sans-serif", marginTop: "2px" }}>
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Status Tabs */}
        <div style={{
          display: "flex", gap: "6px", padding: "4px", borderRadius: "12px",
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
          flexDirection: isRTL ? "row-reverse" : "row",
        }}>
          {tabs.map((tab) => {
            const isActive = activeStatusTab === tab.key;
            return (
              <button key={tab.key} onClick={() => setActiveStatusTab(tab.key)} style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                gap: "6px", padding: "8px 12px", borderRadius: "9px", border: "none",
                background: isActive ? (isDark ? "rgba(255,255,255,0.1)" : "#fff") : "transparent",
                color: isActive ? (isDark ? "#f1f5f9" : "#1e293b") : (isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)"),
                fontSize: "12px", fontWeight: isActive ? 600 : 500, cursor: "pointer",
                fontFamily: "'Cairo', sans-serif", transition: "all 0.2s ease",
                boxShadow: isActive ? (isDark ? "0 2px 8px rgba(0,0,0,0.3)" : "0 2px 8px rgba(0,0,0,0.06)") : "none",
                whiteSpace: "nowrap",
              }}>
                {tab.label}
                {tab.count > 0 && (
                  <span style={{
                    fontSize: "10px", fontWeight: 700, padding: "1px 6px",
                    borderRadius: "6px", background: isActive ? `${tab.color}20` : "transparent",
                    color: tab.color,
                  }}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Request Cards / Skeletons / Empty State */}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {overtimeLoading ? (
            <>
              <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
              <style>{`@keyframes otPulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
            </>
          ) : filtered.length > 0 ? (
            filtered.map((record, idx) => (
              <div key={record.id} style={{
                animation: `otFadeIn 0.3s ease ${idx * 0.04}s both`,
              }}>
                <OvertimeRequestCard
                  record={record} theme={theme} isRTL={isRTL}
                  t={t} language={language} onClick={onSelectRequest}
                  employeeProfilePics={employeeProfilePics}
                />
              </div>
            ))
          ) : (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", padding: "48px 24px", textAlign: "center",
            }}>
              <div style={{
                width: 64, height: 64, borderRadius: 16,
                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
              }}>
                <Inbox size={28} color={isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)"} />
              </div>
              <h4 style={{
                margin: "0 0 6px", fontSize: "15px", fontWeight: 600,
                color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                fontFamily: "'Cairo', sans-serif",
              }}>
                {t.noOvertimeRequests}
              </h4>
              <p style={{
                margin: 0, fontSize: "13px", maxWidth: 240,
                color: isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
                fontFamily: "'Cairo', sans-serif",
              }}>
                {emptyMessage}
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes otFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );
};

export default OvertimeRequestsList;
